import { MonthlyStats } from '../../types/property';
import { formatCurrency } from '../../utils/format';
import { Tooltip } from '../ui/Tooltip';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

interface MonthlyDataTableProps {
  data: MonthlyStats[];
  onEdit?: (data: MonthlyStats) => void;
  onDelete?: (id: string) => void;
}

export const MonthlyDataTable = ({ data, onEdit, onDelete }: MonthlyDataTableProps) => {
  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const tooltips = {
    revenue: 'Ingresos totales generados durante el mes, incluyendo todas las reservas',
    cleaningCosts: 'Gastos asociados a la limpieza y mantenimiento de la propiedad',
    inmohostCosts: 'Comisiones y gastos de gestión de INMOHOST',
    profit: 'Beneficio neto después de restar todos los gastos a los ingresos',
    occupancyRate: 'Porcentaje de días del mes en los que la propiedad estuvo ocupada',
    reservations: 'Número total de reservas realizadas durante el mes',
    averageNightPrice: 'Precio medio por noche calculado sobre todas las reservas del mes',
    averageStayDuration: 'Duración media de las estancias en noches'
  };

  const sortedData = [...data].sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Fecha
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center space-x-1">
                <span>Facturación</span>
                <Tooltip content={tooltips.revenue}>
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                </Tooltip>
              </div>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center space-x-1">
                <span>Gastos Limpieza</span>
                <Tooltip content={tooltips.cleaningCosts}>
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                </Tooltip>
              </div>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center space-x-1">
                <span>Gastos INMOHOST</span>
                <Tooltip content={tooltips.inmohostCosts}>
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                </Tooltip>
              </div>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center space-x-1">
                <span>Beneficio</span>
                <Tooltip content={tooltips.profit}>
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                </Tooltip>
              </div>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center space-x-1">
                <span>Ocupación</span>
                <Tooltip content={tooltips.occupancyRate}>
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                </Tooltip>
              </div>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center space-x-1">
                <span>Reservas</span>
                <Tooltip content={tooltips.reservations}>
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                </Tooltip>
              </div>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center space-x-1">
                <span>Precio/Noche</span>
                <Tooltip content={tooltips.averageNightPrice}>
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                </Tooltip>
              </div>
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center space-x-1">
                <span>Estancia</span>
                <Tooltip content={tooltips.averageStayDuration}>
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                </Tooltip>
              </div>
            </th>
            {(onEdit || onDelete) && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedData.map((item) => (
            <tr key={item.id} className="hover:bg-gray-50">
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {months[item.month - 1]} {item.year}
                </div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900 font-medium">{formatCurrency(item.revenue)}</div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{formatCurrency(item.cleaningCosts)}</div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{formatCurrency(item.inmohostCosts)}</div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900 font-medium">{formatCurrency(item.profit)}</div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {item.occupancyRate}%
                  </span>
                </div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{item.reservations}</div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{formatCurrency(item.averageNightPrice)}</div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{item.averageStayDuration}n</div>
              </td>
              {(onEdit || onDelete) && (
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex space-x-3">
                    {onEdit && (
                      <button
                        onClick={() => onEdit(item)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                      >
                        Editar
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => onDelete(item.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                      >
                        Borrar
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};